import React, { useMemo } from 'react';
import {
    AreaChart, Area, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Legend,
} from 'recharts';

const CHANNEL_COLORS = {
    sms: '#8B5CF6',
    email: '#EC4899',
    whatsapp: '#22c55e',
    browser: '#3B82F6',
    voice: '#f59e0b',
    viber: '#6366f1',
};

const tooltipStyle = {
    backgroundColor: '#1e293b',
    border: '1px solid #334155',
    borderRadius: 6,
    color: '#e2e8f0',
    fontSize: 12,
};

// BUG-107: see ChannelActivityCard for the rationale — empty cards become
// unambiguous when paired with a last-event indicator.
function formatLastEvent(ts) {
    if (!ts) return 'No data yet';
    const d = new Date(ts);
    if (Number.isNaN(d.getTime())) return 'No data yet';
    return `Last activity: ${d.toLocaleString(undefined, {
        month: 'short', day: 'numeric', hour: 'numeric', minute: '2-digit',
    })}`;
}

/**
 * RecipientsByChannelCard — stacked area chart of recipients by channel over time.
 * Expects `data` as an array of { date, channel, count }.
 */
export default function RecipientsByChannelCard({ data, lastEventAt }) {
    // Pivot flat list into { date, sms: N, email: N, ... }
    const { chartData, channels } = useMemo(() => {
        const byDate = {};
        const channelSet = new Set();

        (data || []).forEach(d => {
            const ch = d.channel.toLowerCase();
            channelSet.add(ch);
            if (!byDate[d.date]) byDate[d.date] = {};
            byDate[d.date][ch] = (byDate[d.date][ch] || 0) + d.count;
        });

        const channels = Array.from(channelSet).sort();
        const chartData = Object.keys(byDate)
            .sort()
            .map(date => {
                const row = { date: date.slice(5) }; // "03-01"
                channels.forEach(ch => { row[ch] = byDate[date][ch] || 0; });
                return row;
            });

        return { chartData, channels };
    }, [data]);

    const hasData = chartData.length > 0;

    return (
        <div style={styles.card}>
            <h3 style={styles.title}>Recipients by Channel</h3>
            <p style={styles.subtitle}>Channel activity — last 30 days</p>
            {hasData ? (
                <ResponsiveContainer width="100%" height={200}>
                    <AreaChart data={chartData} margin={{ top: 8, right: 12, left: -20, bottom: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke="#334155" vertical={false} />
                        <XAxis
                            dataKey="date"
                            tick={{ fill: '#64748B', fontSize: 10 }}
                            axisLine={{ stroke: '#334155' }}
                            tickLine={false}
                            interval={Math.max(0, Math.floor(chartData.length / 8) - 1)}
                        />
                        <YAxis
                            tick={{ fill: '#64748B', fontSize: 11 }}
                            axisLine={false}
                            tickLine={false}
                            allowDecimals={false}
                        />
                        <Tooltip contentStyle={tooltipStyle} />
                        <Legend
                            iconType="circle"
                            iconSize={8}
                            wrapperStyle={{ fontSize: 12, color: '#CBD5E1' }}
                        />
                        {channels.map(ch => (
                            <Area
                                key={ch}
                                type="monotone"
                                dataKey={ch}
                                stackId="1"
                                stroke={CHANNEL_COLORS[ch] || '#94A3B8'}
                                fill={CHANNEL_COLORS[ch] || '#94A3B8'}
                                fillOpacity={0.35}
                                strokeWidth={1.5}
                            />
                        ))}
                    </AreaChart>
                </ResponsiveContainer>
            ) : (
                <p style={styles.empty}>No channel activity data for the last 30 days</p>
            )}
            {/* BUG-107: tiny diagnostic so empty cards are unambiguous */}
            <div style={styles.diagnostic}>{formatLastEvent(lastEventAt)}</div>
        </div>
    );
}

const styles = {
    card: {
        background: '#1e3a5f',
        border: '1px solid #2d4a6f',
        borderRadius: 8,
        padding: 20,
    },
    title: {
        margin: 0,
        fontSize: 14,
        fontWeight: 600,
        color: '#FFFFFF',
    },
    subtitle: {
        margin: '4px 0 12px',
        fontSize: 12,
        color: '#94A3B8',
    },
    empty: {
        color: '#64748B',
        fontSize: 13,
        textAlign: 'center',
        padding: '30px 0',
    },
    diagnostic: {
        marginTop: 8,
        fontSize: 11,
        color: '#64748B',
        fontStyle: 'italic',
    },
};
